/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { authClientsSetMcpServerUpstreamAuthRequirement } from "../funcs/authClientsSetMcpServerUpstreamAuthRequirement.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { McpServerAuthentication } from "../models/components/mcpserverauthentication.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SetMcpServerUpstreamAuthRequirementRequest,
  SetMcpServerUpstreamAuthRequirementSecurity,
} from "../models/operations/setmcpserverupstreamauthrequirement.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type SetMcpServerUpstreamAuthRequirementMutationVariables = {
  request: SetMcpServerUpstreamAuthRequirementRequest;
  security?: SetMcpServerUpstreamAuthRequirementSecurity | undefined;
  options?: RequestOptions;
};

export type SetMcpServerUpstreamAuthRequirementMutationData =
  McpServerAuthentication;

export type SetMcpServerUpstreamAuthRequirementMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * setMcpServerUpstreamAuthRequirement authClients
 *
 * @remarks
 * Declare whether an MCP server's upstream needs authentication. Where typed auth client resolution is enforced, a server that needs none is served without upstream credentials and a server that needs it and offers no enabled client is unavailable until setup is complete; elsewhere, including in shadow mode, the server's existing upstream configuration keeps serving. Not available for hosted toolsets. Requires org:admin.
 */
export function useSetMcpServerUpstreamAuthRequirementMutation(
  options?: MutationHookOptions<
    SetMcpServerUpstreamAuthRequirementMutationData,
    SetMcpServerUpstreamAuthRequirementMutationError,
    SetMcpServerUpstreamAuthRequirementMutationVariables
  >,
): UseMutationResult<
  SetMcpServerUpstreamAuthRequirementMutationData,
  SetMcpServerUpstreamAuthRequirementMutationError,
  SetMcpServerUpstreamAuthRequirementMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildSetMcpServerUpstreamAuthRequirementMutation(client, options),
    ...options,
  });
}

export function mutationKeySetMcpServerUpstreamAuthRequirement(): MutationKey {
  return ["@gram/client", "authClients", "setMcpServerUpstreamAuthRequirement"];
}

export function buildSetMcpServerUpstreamAuthRequirementMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: SetMcpServerUpstreamAuthRequirementMutationVariables,
  ) => Promise<SetMcpServerUpstreamAuthRequirementMutationData>;
} {
  return {
    mutationKey: mutationKeySetMcpServerUpstreamAuthRequirement(),
    mutationFn: function setMcpServerUpstreamAuthRequirementMutationFn({
      request,
      security,
      options,
    }): Promise<SetMcpServerUpstreamAuthRequirementMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(authClientsSetMcpServerUpstreamAuthRequirement(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
